import classNames from 'clsx';
import { parse } from 'qs';
import React, { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initProducts, initProductsForAnonymous } from 'reactApp/modules/products/products.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { FooterNestle } from 'reactApp/sections/Nestle/FooterNestle/FooterNestle';
import { FormWrapper } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper';
import { HeaderNestle } from 'reactApp/sections/Nestle/HeaderNestle/HeaderNestle';
import { sendNestleLandingAnalytics, sendNestleLandingTmrGoal } from 'reactApp/sections/Nestle/NestleLanding.analytics';
import { PetsCardCarousel } from 'reactApp/sections/Nestle/PetsCardCarousel/PetsCardCarousel';
import { PrizeBlock } from 'reactApp/sections/Nestle/PrizeBlock/PrizeBlock';
import { PrizeInfoBlock } from 'reactApp/sections/Nestle/PrizeInfoBlock/PrizeInfoBlock';
import { ResultTable } from 'reactApp/sections/Nestle/ResultTable/ResultTable';
import { TariffsBlock } from 'reactApp/sections/Nestle/TariffsBlock/TariffsBlock';
import { scrollToTop } from 'reactApp/utils/helpers';

import styles from './NestleLanding.css';

export const NestleLanding = (): ReactElement => {
    const dispatch = useDispatch();

    const { tariffs, success } = parse(window.location.search, { ignoreQueryPrefix: true });

    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const [isFilledForm, setIsFilledForm] = useState<boolean>(Boolean(tariffs));
    const [isPaid, setIsPaid] = useState<boolean>(false);

    const tariffsRef = useRef<HTMLDivElement>(null);
    const petsRef = useRef<HTMLDivElement>(null);

    const onSuccessPayment = useCallback(() => {
        setIsPaid(true);
    }, []);

    const onSuccessFill = useCallback(() => {
        if (isAnonymous) {
            dispatch(initProductsForAnonymous());
        }
        dispatch(initProducts());
        setIsFilledForm(true);
        scrollToTop();
    }, []);

    const scrollToTariffs = useCallback(() => {
        tariffsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const scrollToPets = useCallback(() => {
        petsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const onFillFormAgain = useCallback(() => {
        setIsFilledForm(false);
        setIsPaid(false);
    }, []);

    const content = useMemo(() => {
        if (isPaid && isFilledForm) {
            return (
                <>
                    <PrizeInfoBlock
                        isPaid
                        onFillFormAgain={onFillFormAgain}
                        scrollToTariffs={scrollToTariffs}
                        scrollToPets={scrollToPets}
                    />
                </>
            );
        }

        if (isFilledForm) {
            return (
                <>
                    <PrizeInfoBlock onFillFormAgain={onFillFormAgain} scrollToTariffs={scrollToTariffs} scrollToPets={scrollToPets} />
                    <TariffsBlock ref={tariffsRef} onSuccessPayment={onSuccessPayment} />
                </>
            );
        }

        return (
            <>
                <FormWrapper onComplete={onSuccessFill} />
                <PrizeBlock />
            </>
        );
    }, [isFilledForm, isPaid, onSuccessFill]);

    useEffect(() => {
        if (tariffs) {
            scrollToTariffs();
        }

        if (success) {
            sendNestleLandingTmrGoal('login-succes');
        }
    }, []);

    useEffect(() => {
        sendNestleLandingTmrGoal('landing-view');
        sendNestleLandingAnalytics({
            eventCategory: 'page_hit',
        });
    }, []);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_mobile]: isMobile,
            })}
        >
            <HeaderNestle />
            {content}
            <PetsCardCarousel ref={petsRef} />
            <ResultTable />
            <FooterNestle />
        </div>
    );
};

NestleLanding.displayName = 'NestleLanding';

export default NestleLanding;
